import React, { PropsWithChildren, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { ActionGoalId } from 'types/Goal';
import { Link } from 'react-router-dom';
import { usePanel } from 'contexts/panelContext';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import SigningField from 'components/_common/panel/SigningField';
import { usePair } from 'contexts/pairContext';
import { PageLoading } from 'components/PageLoading';
import PageMissing from 'components/PageMissing';
import useGoals from 'hooks/useGoals';
import { userDisplayName } from 'utils/userDisplayName';
import { useAuth } from 'contexts/authContext';
import { CurrentUser } from 'types/CurrentUser';
import { Page } from 'components/_new/Page';
import { Button } from 'components/_new/Button';
import { Icon } from 'components/_new/Icon';
import BusinessCard from 'components/_common/panel/BusinessCard';
import { MessageBox } from 'components/_new/MessageBox';
import { Box, BoxSection } from 'components/_new/Box';
import { ContentSection } from 'components/_new/Content/ContentSection';
import GoalItem from './GoalItem';
import { useCounterpartApplication } from '../../../contexts/useCounterpartApplication';
import { Dialog } from '../../_new/Dialog';
import { LinkDocArticleWithIconWrapper } from '../sessions/components/LinkDocArticleWithIconWrapper';

const GoalsWrapper = () => {
	const { t } = useTranslation();

	const { currentUser } = useAuth();
	const { panelType } = usePanel();
	const { pair } = usePair();
	const [editedGoal, setEditedGoal] = useState<number | null>(null);

	const {
		getGoals,
		saveNewGoal,
		updateGoal,
		deleteGoal,
		signGoals,
		toggleGoalStep,
		fetchedGoals,
		goalLoading,
		goalError,
	} = useGoals(pair.id, {
		onMutationSuccess: () => setEditedGoal(null),
	});

	const { currentProgram, currentApplicationData, todosData } = usePanel();

	const [showProfileModal, setShowProfileModal] = useState(false);
	const [shakeButton, setShakeButton] = useState(false);
	const { data: counterpartData } = useCounterpartApplication();
	const pairFullName = userDisplayName(pair?.application.programMembership.user);
	const userFullName = userDisplayName(currentUser as CurrentUser);
	const menteeFullName = pair?.application.applicationRole === 'mentee' ? pairFullName : userFullName;
	const menteeData = currentApplicationData.applicationRole === 'mentee' ? currentApplicationData : counterpartData;

	const currentGoals = useMemo(() => {
		const goals = fetchedGoals?.goals ?? [];

		if (editedGoal === ActionGoalId.new) {
			const hasNewGoal = goals.some((goal) => goal.id === ActionGoalId.new);
			if (!hasNewGoal) {
				return [
					...goals,
					{
						id: ActionGoalId.new,
						goal: '',
						change: '',
						achivmentPath: '',
						priorities: '',
						notes: '',
						areaOfInfluence: '',
						deadlineAndReview: '',
					},
				];
			}
		}

		return goals;
	}, [fetchedGoals?.goals, editedGoal]);

	const surveyTodo = useMemo(() => {
		return (
			todosData.pair
				.flatMap((p) => p.items)
				.find((item) => item.type === 'evaluation_survey_required' && item.variables.pairId === pair.id) || null
		);
	}, [todosData.pair, pair.id]);

	const addNewGoal = () => {
		setEditedGoal(ActionGoalId.new);
	};

	const hasRealGoals = useMemo(() => {
		return currentGoals.filter((goal) => goal.id !== ActionGoalId.new).length > 0;
	}, [currentGoals]);

	React.useEffect(() => {
		if (!hasRealGoals && editedGoal === null) {
			const timer = setTimeout(() => {
				setShakeButton(true);
				setTimeout(() => setShakeButton(false), 600);
			}, 500);

			return () => clearTimeout(timer);
		}
		return undefined;
	}, [hasRealGoals, editedGoal]);

	if (goalError) {
		return <PageMissing />;
	}

	if (goalLoading || !fetchedGoals || !currentProgram) {
		return <PageLoading />;
	}

	const disabledTooltip = !hasRealGoals ? t('userPanel.goal.addGoalToSign') : undefined;

	return (
		<>
			<Helmet title={`${t('userPanel.navigation.mentoringPlan')}: ${menteeFullName}`} />
			<Dialog
				visible={showProfileModal}
				onHide={() => setShowProfileModal(false)}
				className="p-dialog-buissnescard"
				title={t('userPanel.pairing.personalDetailsPair', {
					type: t('misc.mentees'),
				})}
				size="lg"
			>
				{menteeData && <BusinessCard data={menteeData} disableMessage />}
			</Dialog>

			<SubPageTitle
				title={
					<div className="flex flex-row">
						{`${t('userPanel.navigation.mentoringPlan')}: ${menteeFullName}`}
						<Button
							className="ml-2 p-0 cursor-pointer"
							onClick={() => setShowProfileModal(true)}
							icon="arrow-up-right-from-square"
							variant="primary-text"
							iconStyle="solid"
							iconOnly
							label=""
						/>
					</div>
				}
			/>

			<Page className="flex flex-column gap-4">
				<ContentSection
					title={t('userPanel.goal.developmentPlan')}
					titleIconProps={{ name: 'bullseye' }}
					className="lh-1-6"
				>
					<Box variant="blue">
						<BoxSection>
							<div>
								<div className="mb-1">{t('userPanel.goal.developmentPlanHeader')}:</div>
								<ul className="list arrow-list">
									<li>{`${t('userPanel.goal.increasesEffectiveness')},`}</li>
									<li>{`${t('userPanel.goal.helpsFocusSessions')},`}</li>
									<li>{`${t('userPanel.goal.enablesMonitoring')},`}</li>
									<li>{`${t('userPanel.goal.strengthensResponsibility')},`}</li>
								</ul>
							</div>
						</BoxSection>
					</Box>

					<div>
						<p>{t('userPanel.goal.recommendsGoals')}</p>
						<p>
							{t('userPanel.goal.planCreatedInDialog')} <b>{t('userPanel.goal.menteeDefines')}</b>
						</p>
						<p>
							<Trans
								t={t}
								i18nKey="userPanel.goal.seeGuide"
								components={[
									<LinkDocArticleWithIconWrapper
										articleLabel="goals-in-mentoring"
										className="text-nowrap"
									/>,
								]}
							/>
						</p>
					</div>
					{surveyTodo &&
						currentApplicationData.applicationRole === 'mentee' &&
						currentProgram.organization && (
							<MessageBox variant="rose" highlight className="w-full">
								<div className="flex justify-content-between align-items-center w-full gap-2">
									<div className="flex-1">{t('userPanel.goal.evaluationSurveyBanner')}</div>
									<Link
										to={`/panel/${currentProgram.name}/mentee/pair/${surveyTodo.variables.pairId}/session/${surveyTodo.variables.sessionId}/evaluation-mentee#content`}
										className="text-primary font-bold no-underline flex align-items-center"
									>
										{t('userPanel.goal.evaluationSurveyFill')}
										<Icon name="arrow-right" className="ml-2 text-xs" />
									</Link>
								</div>
							</MessageBox>
						)}
				</ContentSection>

				<ContentSection
					title={`${t('userPanel.goal.yourGoals')}:`}
					titleIconProps={{ name: 'list-ol' }}
					className="lh-1-6"
				>
					<div className="flex flex-column gap-4">
						{currentGoals.map((goalData, index) => (
							<GoalItem
								editedGoal={editedGoal}
								getGoals={getGoals}
								saveNewGoal={saveNewGoal}
								updateGoal={updateGoal}
								deleteGoal={deleteGoal}
								toggleGoalStep={toggleGoalStep}
								key={goalData.id}
								goalData={goalData}
								index={index + 1}
								setEditedGoal={setEditedGoal}
							/>
						))}
					</div>

					{!hasRealGoals && editedGoal === null && (
						<MessageBox message={t('userPanel.goal.noGoalsMessage')} variant="rose" highlight />
					)}

					{editedGoal === null && (
						<div className="flex align-items-center mr-4 font-bold text-xl mt-3 mb-2 justify-content-end">
							<div
								className={`session-date-button-wrapper ${
									shakeButton ? 'session-date-button-shake' : ''
								}`}
							>
								{!hasRealGoals && (
									<div className="box-warning-icon">
										<Icon name="exclamation-circle" className="warning-icon-color" />
									</div>
								)}
								<Button
									label={t('userPanel.contract.addGoal')}
									onClick={addNewGoal}
									icon="plus"
									variant={!hasRealGoals ? 'primary-light-outlined' : 'primary-text'}
									className={!hasRealGoals ? 'shake-animation relative' : ''}
								/>
							</div>
						</div>
					)}
				</ContentSection>

				<ContentSection title={t('userPanel.goal.goalsSignedBy')} titleIconProps={{ name: 'signature' }}>
					<SigningField
						signContract={signGoals}
						menteeSign={fetchedGoals?.goalSignDateMentee}
						mentorSign={fetchedGoals?.goalSignDateMentor}
						panelType={panelType}
						info={t('userPanel.goal.signInfo')}
						disabled={!hasRealGoals}
						disabledTooltip={disabledTooltip}
					/>
				</ContentSection>
			</Page>
		</>
	);
};

export default GoalsWrapper;
